/**
 * Binding to the Flux Agent editor session API under `/api/flux-agent/editor/`. It only moves
 * data in and out of the Rust Worker, which decides who is an editor or admin, approves OAuth
 * requests, issues tokens and builds the setup commands.
 *
 * @module apps/web/src/lib/editorToolsApi
 */

import { request, type ApiResponse } from '@/lib/api';

/** Copy-paste setup for each MCP client, built by the server. */
export interface EditorSetup {
  /** `claude mcp add` command that signs in through the browser. */
  claudeCode: string;
  /** `claude mcp add` command using a personal access token placeholder. */
  claudeCodeWithToken: string;
  /** Steps for Claude Desktop custom connectors. */
  claudeDesktop: string[];
  /** Steps for ChatGPT connectors. */
  chatgpt: string[];
  /** Generic `mcpServers` JSON for clients that read a config file. */
  mcpJson: unknown;
  /** The same JSON with an `Authorization` header placeholder. */
  mcpJsonWithToken: unknown;
}

/** The caller's roles and, for editors and admins, server details. */
export interface EditorStatus {
  /** Holds an active editor grant. */
  editor: boolean;
  /** May grant and revoke editors. */
  admin: boolean;
  /** Signed-in account email; absent for people without access. */
  email?: string;
  /** MCP server URL to add to a client; absent for people without access. */
  mcpUrl?: string;
  /** OAuth issuer. */
  issuer?: string;
  /** The single scope the server grants. */
  scope?: string;
  /** HTTPS hosts OAuth codes may be sent to besides loopback. */
  redirectHosts?: string[];
  /** Longest personal token lifetime in days. */
  patMaxDays?: number;
  /** Default personal token lifetime in days. */
  patDefaultDays?: number;
  /** Setup commands and steps. */
  setup?: EditorSetup;
  /** False when the server's kill switch is on. */
  enabled?: boolean;
}

/** A pending OAuth sign-in request shown on the consent page. */
export interface EditorAuthorization {
  /** Name the client registered with; self-declared, so shown with its redirect host. */
  clientName: string;
  /** Where the code will be delivered. */
  redirectUri: string;
  /** Host of `redirectUri`. */
  redirectHost: string | null;
  /** Scope being granted. */
  scope: string;
  /** `pending`, `approved`, `denied` or `used`. */
  status: string;
  /** True when the request timed out before a decision. */
  expired: boolean;
  /** Whether the signed-in person may approve. */
  editor: boolean;
  /** Signed-in account email. */
  email: string;
}

/** A personal token or OAuth app connection. */
export interface EditorConnection {
  /** Grant id used to revoke it. */
  id: string;
  /** `personal_token` or `oauth_app`. */
  type: 'personal_token' | 'oauth_app';
  /** Token label or client name. */
  name: string | null;
  /** Creation time, RFC 3339. */
  createdAt: string | null;
  /** Last use, RFC 3339. */
  lastUsedAt: string | null;
  /** When it stops working, RFC 3339. */
  expiresAt: string | null;
}

/** A newly created personal access token, shown once. */
export interface CreatedEditorToken {
  /** Grant id. */
  id: string;
  /** Label. */
  name: string;
  /** The secret; it is never shown again. */
  token: string;
  /** Expiry, RFC 3339. */
  expiresAt: string;
  /** Ready-to-run Claude Code command with the token filled in. */
  claudeCode: string;
}

/** One editor grant in the admin list. */
export interface EditorGrant {
  /** Fluxability user id. */
  userId: string;
  /** Account email. */
  email: string;
  /** Admin who granted it. */
  grantedBy: string;
  /** Grant time, RFC 3339. */
  grantedAt: string;
  /** Revocation time, RFC 3339. */
  revokedAt: string | null;
  /** Admin who revoked it. */
  revokedBy: string | null;
  /** Optional note. */
  note: string | null;
  /** Whether the grant is active. */
  active: boolean;
}

/** One audit row. */
export interface EditorAuditEntry {
  /** Row id. */
  id: string;
  /** Time, RFC 3339. */
  at: string;
  /** Acting email. */
  actor: string | null;
  /** Dotted action such as `proposal.apply`. */
  action: string;
  /** `ok`, `denied`, `conflict`, `invalid` or `error`. */
  outcome: string;
  /** Library path. */
  path: string | null;
  /** Proposal id. */
  proposalId: string | null;
  /** SHA-256 before the change. */
  beforeSha256: string | null;
  /** SHA-256 after the change. */
  afterSha256: string | null;
  /** Library commit. */
  commit: string | null;
  /** Editor's rationale. */
  rationale: string | null;
  /** Refusal code. */
  errorCode: string | null;
}

/** Base path of the session API. */
const BASE = '/flux-agent/editor';

/** Session API calls; each returns the normalized {@link ApiResponse}. */
export const editorToolsApi = {
  /** GET `/status`. */
  status: (): Promise<ApiResponse<EditorStatus>> => request(`${BASE}/status`),
  /** GET `/authorizations/:id`. */
  authorization: (id: string): Promise<ApiResponse<EditorAuthorization>> =>
    request(`${BASE}/authorizations/${encodeURIComponent(id)}`),
  /** POST `/authorizations/:id/approve`; returns where to send the browser. */
  approve: (
    id: string
  ): Promise<ApiResponse<{ /** Client callback URL with the code. */ redirectTo: string }>> =>
    request(`${BASE}/authorizations/${encodeURIComponent(id)}/approve`, { method: 'POST' }),
  /** POST `/authorizations/:id/deny`; returns where to send the browser. */
  deny: (
    id: string
  ): Promise<ApiResponse<{ /** Client callback URL with the error. */ redirectTo: string }>> =>
    request(`${BASE}/authorizations/${encodeURIComponent(id)}/deny`, { method: 'POST' }),
  /** GET `/tokens`. */
  connections: (): Promise<
    ApiResponse<{ /** Active connections. */ connections: EditorConnection[] }>
  > => request(`${BASE}/tokens`),
  /** POST `/tokens`. */
  createToken: (name: string, expiresInDays: number): Promise<ApiResponse<CreatedEditorToken>> =>
    request(`${BASE}/tokens`, { method: 'POST', body: JSON.stringify({ name, expiresInDays }) }),
  /** DELETE `/tokens/:id`. */
  revokeConnection: (id: string): Promise<ApiResponse<unknown>> =>
    request(`${BASE}/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** GET `/editors` (admins). */
  editors: (): Promise<ApiResponse<{ /** Every grant. */ editors: EditorGrant[] }>> =>
    request(`${BASE}/editors`),
  /** POST `/editors` (admins). */
  grant: (email: string, note?: string): Promise<ApiResponse<unknown>> =>
    request(`${BASE}/editors`, { method: 'POST', body: JSON.stringify({ email, note }) }),
  /** DELETE `/editors/:userId` (admins). */
  revokeEditor: (userId: string): Promise<ApiResponse<unknown>> =>
    request(`${BASE}/editors/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
  /** GET `/audit`. */
  audit: (): Promise<ApiResponse<{ /** Newest first. */ entries: EditorAuditEntry[] }>> =>
    request(`${BASE}/audit`),
};
